import React from 'react';

function fmtUsd(n: number | null | undefined): string {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v)) return '—';
  const sign = v >= 0 ? '' : '-';
  return `${sign}$${Math.abs(v).toFixed(2)}`;
}

function fmtLeverage(n: number | null | undefined): string {
  const v = Number(n ?? 0);
  if (!v || !Number.isFinite(v)) return '—';
  const rounded = Math.round(v * 100) / 100;
  return `${rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(2)}x`;
}

export const fieldLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  fontWeight: 700,
  color: '#64748b',
  marginBottom: 6,
  textTransform: 'uppercase',
  letterSpacing: 0.3,
};

export const fieldInputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 8,
  border: '1px solid #e2e8f0',
  fontSize: 13,
  boxSizing: 'border-box',
};

const ACTIVITY_BOT_AMOUNT_MIN = 1;
const ACTIVITY_BOT_AMOUNT_MAX = 500;
const ACTIVITY_BOT_AMOUNT_DIAL_STEP = 0.5;
export const ACTIVITY_BOT_MAX_LEVERAGE = 10;
const ACTIVITY_BOT_LEVERAGE_SNAP = [1, 2, 3, 5, 10] as const;
const ACTIVITY_BOT_MARGIN_SAFETY_FACTOR = 1.3;

export function clampActivityBotAmount(value: number): number {
  if (!Number.isFinite(value)) return ACTIVITY_BOT_AMOUNT_MIN;
  const rounded = Math.round(value * 2) / 2;
  return Math.max(ACTIVITY_BOT_AMOUNT_MIN, Math.min(ACTIVITY_BOT_AMOUNT_MAX, rounded));
}

export function snapActivityBotLeverage(value: number): number {
  if (!Number.isFinite(value)) return 1;
  const clamped = Math.max(1, Math.min(ACTIVITY_BOT_MAX_LEVERAGE, Math.round(value)));
  let best: number = ACTIVITY_BOT_LEVERAGE_SNAP[0];
  let bestDiff = Math.abs(clamped - best);
  for (const snap of ACTIVITY_BOT_LEVERAGE_SNAP) {
    const diff = Math.abs(clamped - snap);
    if (diff < bestDiff) {
      best = snap;
      bestDiff = diff;
    }
  }
  return best;
}

function stepActivityBotLeverage(current: number, direction: -1 | 1): number {
  const snapped = snapActivityBotLeverage(current);
  const idx = ACTIVITY_BOT_LEVERAGE_SNAP.findIndex(v => v === snapped);
  const safeIdx = idx >= 0 ? idx : 0;
  const nextIdx = Math.max(0, Math.min(ACTIVITY_BOT_LEVERAGE_SNAP.length - 1, safeIdx + direction));
  return ACTIVITY_BOT_LEVERAGE_SNAP[nextIdx];
}

function estimateActivityBotMarginPerLeg(amountUsd: number, leverage: number): number {
  const amount = Number(amountUsd);
  const lev = Math.max(1, Number(leverage) || 1);
  if (!Number.isFinite(amount) || amount <= 0) return 0;
  return (amount / lev) * ACTIVITY_BOT_MARGIN_SAFETY_FACTOR;
}

const activityBotDialBtnStyle: React.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: 999,
  border: '1px solid #cbd5e1',
  background: '#fff',
  color: '#334155',
  fontSize: 18,
  fontWeight: 700,
  cursor: 'pointer',
  lineHeight: 1,
};

interface ActivityBotDialControlProps {
  label: string;
  value: string;
  min: number;
  max: number;
  dialStep: number;
  inputStep?: number;
  snapValues?: readonly number[];
  formatDisplay: (value: number) => string;
  onChange: (next: string) => void;
  onDialChange?: (next: number) => number;
  onStepDown?: (current: number) => number;
  onStepUp?: (current: number) => number;
  hint?: string;
  inputSuffix?: string;
}

const ActivityBotDialControl: React.FC<ActivityBotDialControlProps> = ({
  label,
  value,
  min,
  max,
  dialStep,
  inputStep = dialStep,
  formatDisplay,
  onChange,
  onDialChange,
  onStepDown,
  onStepUp,
  hint,
  inputSuffix,
  snapValues,
}) => {
  const parsed = Number(value);
  const resolvedValue = Number.isFinite(parsed)
    ? (onDialChange ? onDialChange(parsed) : parsed)
    : (snapValues?.[0] ?? min);
  const dialMin = snapValues ? 0 : min;
  const dialMax = snapValues ? Math.max(0, snapValues.length - 1) : max;
  let safeDial = snapValues
    ? snapValues.findIndex(v => v === snapActivityBotLeverage(resolvedValue))
    : resolvedValue;
  if (snapValues && safeDial < 0) safeDial = 0;
  if (!snapValues) {
    safeDial = Math.max(min, Math.min(max, resolvedValue));
  }
  const safe = snapValues ? snapValues[safeDial] ?? snapValues[0] : safeDial;
  const ratio = dialMax > dialMin ? (safeDial - dialMin) / (dialMax - dialMin) : 0;
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - Math.max(0, Math.min(1, ratio)));

  const applyValue = (next: number) => {
    if (snapValues) {
      const idx = Math.max(0, Math.min(snapValues.length - 1, Math.round(next)));
      onChange(String(snapValues[idx]));
      return;
    }
    const normalized = onDialChange ? onDialChange(next) : next;
    const clamped = Math.max(min, Math.min(max, normalized));
    onChange(String(clamped));
  };

  const stepDown = () => {
    if (snapValues && onStepDown) {
      onChange(String(onStepDown(safe)));
      return;
    }
    if (snapValues) {
      applyValue(safeDial - 1);
      return;
    }
    const next = onStepDown ? onStepDown(safe) : safe - dialStep;
    applyValue(next);
  };

  const stepUp = () => {
    if (snapValues && onStepUp) {
      onChange(String(onStepUp(safe)));
      return;
    }
    if (snapValues) {
      applyValue(safeDial + 1);
      return;
    }
    const next = onStepUp ? onStepUp(safe) : safe + dialStep;
    applyValue(next);
  };

  return (
    <div
      style={{
        border: '1px solid #e2e8f0',
        borderRadius: 12,
        padding: '12px 14px 14px',
        background: '#fff',
      }}
    >
      <label style={{ ...fieldLabelStyle, marginBottom: 10 }}>{label}</label>
      <div style={{ position: 'relative', width: 132, height: 132, margin: '0 auto' }}>
        <svg width="132" height="132" viewBox="0 0 100 100" aria-hidden="true">
          <circle cx="50" cy="50" r={radius} fill="none" stroke="#e2e8f0" strokeWidth="7" />
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke="#086afb"
            strokeWidth="7"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            transform="rotate(-90 50 50)"
            style={{ transition: 'stroke-dashoffset 0.15s ease' }}
          />
        </svg>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 18,
            fontWeight: 800,
            color: '#1e293b',
            fontFamily: 'Nunito, sans-serif',
            pointerEvents: 'none',
          }}
        >
          {formatDisplay(safe)}
        </div>
        <input
          type="range"
          min={dialMin}
          max={dialMax}
          step={1}
          value={safeDial}
          onChange={e => applyValue(Number(e.target.value))}
          aria-label={label}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            margin: 0,
            opacity: 0,
            cursor: 'pointer',
          }}
        />
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginTop: 10 }}>
        <button type="button" onClick={stepDown} aria-label={`Decrease ${label}`} style={activityBotDialBtnStyle}>
          −
        </button>
        <button type="button" onClick={stepUp} aria-label={`Increase ${label}`} style={activityBotDialBtnStyle}>
          +
        </button>
      </div>
      <div style={{ marginTop: 10 }}>
        <input
          type="number"
          min={min}
          max={max}
          step={inputStep}
          value={value}
          onChange={e => {
            const next = Number(e.target.value);
            if (!Number.isFinite(next)) {
              onChange(e.target.value);
              return;
            }
            applyValue(next);
          }}
          style={{ ...fieldInputStyle, textAlign: 'center' }}
        />
        {inputSuffix && (
          <div style={{ marginTop: 4, fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>{inputSuffix}</div>
        )}
      </div>
      {hint && (
        <div style={{ marginTop: 8, fontSize: 11, color: '#94a3b8', lineHeight: 1.45 }}>{hint}</div>
      )}
    </div>
  );
};

interface ActivityBotMarginPreviewProps {
  amountUsd: string;
  leverage: string;
  totalPositions: string;
}

const ActivityBotMarginPreview: React.FC<ActivityBotMarginPreviewProps> = ({
  amountUsd,
  leverage,
  totalPositions,
}) => {
  const amount = Number(amountUsd);
  const lev = snapActivityBotLeverage(Number(leverage));
  const legs = Number(totalPositions);
  if (!Number.isFinite(amount) || amount <= 0 || !Number.isFinite(lev) || lev < 1) {
    return null;
  }
  const marginPerLeg = estimateActivityBotMarginPerLeg(amount, lev);
  const legCount = Number.isFinite(legs) && legs > 0 ? legs : 1;
  const cycleNotional = amount * legCount;
  const cycleMarginEst = marginPerLeg * legCount;

  return (
    <div
      style={{
        marginTop: 12,
        padding: '12px 14px',
        borderRadius: 10,
        border: '1px solid #bfdbfe',
        background: 'linear-gradient(180deg, #f8fbff 0%, #eff6ff 100%)',
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 700, color: '#086afb', textTransform: 'uppercase', letterSpacing: 0.3 }}>
        Margin preview
      </div>
      <div style={{ marginTop: 8, fontSize: 13, color: '#334155', lineHeight: 1.5 }}>
        <strong>Est. margin per leg:</strong>{' '}
        ~{fmtUsd(marginPerLeg)} at {fmtUsd(amount)} · {fmtLeverage(lev)}
      </div>
      <div style={{ marginTop: 4, fontSize: 13, color: '#334155', lineHeight: 1.5 }}>
        <strong>Cycle notional:</strong> ~{fmtUsd(cycleNotional)} ({legCount} leg{legCount === 1 ? '' : 's'})
        {' · '}
        <strong>est. margin:</strong> ~{fmtUsd(cycleMarginEst)}
      </div>
      <div style={{ marginTop: 8, fontSize: 11, color: '#64748b', lineHeight: 1.45 }}>
        Includes a 30% safety buffer. If KuCoin rejects an order, the bot may auto-raise leverage (up to {ACTIVITY_BOT_MAX_LEVERAGE}x) or lower size.
      </div>
    </div>
  );
};

export interface ActivityBotSizingControlsProps {
  amountUsd: string;
  leverage: string;
  totalPositions: string;
  onAmountChange: (value: string) => void;
  onLeverageChange: (value: string) => void;
}

export const ActivityBotSizingControls: React.FC<ActivityBotSizingControlsProps> = ({
  amountUsd,
  leverage,
  totalPositions,
  onAmountChange,
  onLeverageChange,
}) => (
  <div style={{ gridColumn: '1 / -1' }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 8 }}>
      2) Size &amp; leverage
    </div>
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 12,
      }}
    >
      <ActivityBotDialControl
        label="Amount per position (USD)"
        value={amountUsd}
        min={ACTIVITY_BOT_AMOUNT_MIN}
        max={ACTIVITY_BOT_AMOUNT_MAX}
        dialStep={ACTIVITY_BOT_AMOUNT_DIAL_STEP}
        inputStep={0.01}
        formatDisplay={v => fmtUsd(v)}
        onChange={onAmountChange}
        onDialChange={clampActivityBotAmount}
        onStepDown={v => clampActivityBotAmount(v - ACTIVITY_BOT_AMOUNT_DIAL_STEP)}
        onStepUp={v => clampActivityBotAmount(v + ACTIVITY_BOT_AMOUNT_DIAL_STEP)}
        inputSuffix="Type exact USD if needed"
      />
      <ActivityBotDialControl
        label="Leverage (futures)"
        value={leverage}
        min={1}
        max={ACTIVITY_BOT_MAX_LEVERAGE}
        dialStep={1}
        inputStep={1}
        snapValues={ACTIVITY_BOT_LEVERAGE_SNAP}
        formatDisplay={v => fmtLeverage(v)}
        onChange={onLeverageChange}
        onDialChange={snapActivityBotLeverage}
        onStepDown={v => stepActivityBotLeverage(v, -1)}
        onStepUp={v => stepActivityBotLeverage(v, 1)}
        hint="Applied to new legs opened after save. Max 10x. Existing open positions keep their entry leverage."
        inputSuffix="Snaps to 1, 2, 3, 5, 10 (max 10x)"
      />
    </div>
    <ActivityBotMarginPreview
      amountUsd={amountUsd}
      leverage={leverage}
      totalPositions={totalPositions}
    />
  </div>
);

